import React from 'react';
/**
 * LearnerViewRouter — Main view routing inside AppShell
 *
 * Extracted from App.tsx in R33 (DDD Fase 6 — App Shell decomposition).
 * Renders the correct page based on currentNavView within the learner shell.
 */
import { HomePage } from '../components/home/HomePage';
import { SubjectsPage } from '../components/subjects/SubjectsPage';
import { ProgressPage } from '../components/progress/ProgressPage';
import { MascotCollectionPage } from '../components/mascot/MascotCollectionPage';
import { LearningLabPage } from '../learningLab';
import { SettingsPage } from '../components/settings/SettingsPage';
import { Card } from '../components/ui/Card';
import { AlertCircle } from 'lucide-react';
import { AssessmentPage, AssessmentReportPage } from '../assessment';
import type { ModuleId, Question } from '../content';
import type { AppState } from './types';
import type { GroupId } from '../config/systemConfig';
import type { NavView } from './hooks/useAppNavigation';
import type { AssessmentConfig, AssessmentReport } from '../assessment';
import type { AccountsState } from '../accounts';
interface LearnerViewRouterProps {
  currentNavView: NavView;
  requiresViewingLearnerSelection: boolean;
  state: AppState;
  selectedGroup: GroupId;
  modules: typeof import('../content/questions/questionLoader').MODULES;
  questions: Question[];
  displayName: string;
  assessmentReport: AssessmentReport | null;
  accounts: AccountsState;
  startingPeriod: number;
  manualPeriod: number | null;
  // Handlers
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: (typeof import('../content/questions/questionLoader').MODULES)[number]) => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onStartMode: (mode: string, familyId?: string) => void;
  onNavigate: (view: NavView) => void;
  onSetStartingPeriod: (period: number) => void;
  onUpdateName: (name: string) => void;
  onSwitchAccount: (accountId: string) => void;
  onLogout: () => void;
  onCancelAssessment: () => void;
  onExitReport: () => void;
  onRetakeAssessment: () => void;
  onViewReport: (report: AssessmentReport) => void;
  onPracticeWeakAreas: () => void;
  onStartPracticeForModule?: (moduleId: ModuleId) => void;
  viewingLearnerId?: string | null;
}
export function LearnerViewRouter({
  currentNavView,
  requiresViewingLearnerSelection,
  state,
  selectedGroup,
  modules,
  questions,
  displayName,
  assessmentReport,
  accounts,
  startingPeriod,
  manualPeriod,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onStartAssessment,
  onStartMode,
  onNavigate,
  onSetStartingPeriod,
  onUpdateName,
  onSwitchAccount,
  onLogout,
  onCancelAssessment,
  onExitReport,
  onRetakeAssessment,
  onViewReport,
  onPracticeWeakAreas,
  onStartPracticeForModule,
  viewingLearnerId
}: LearnerViewRouterProps) {
  // Derive learnerId: for parent/teacher viewing a student, use viewingLearnerId;
  // for a student viewing their own page, use their active account ID.
  const learnerId = viewingLearnerId || accounts.activeAccountId || undefined;
  // Learner selection required for non-settings views
  if (requiresViewingLearnerSelection && ['home', 'modules', 'progress', 'assessment', 'learningLab', 'mascot'].includes(currentNavView)) {
    return <div className="max-w-2xl mx-auto" data-id="element-780">
        <Card className="p-10 text-center border-2 border-orange-100 bg-orange-50/40" data-id="element-781">
          <AlertCircle className="w-10 h-10 text-orange-500 mx-auto mb-4" data-id="element-782" />
          <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-783">
            Selecteer eerst een leerling
          </h2>
          <p className="text-slate-600 font-medium" data-id="element-784">
            Kies in het ouder- of docentportaal een leerling om voortgang,
            dashboard en instellingen te bekijken.
          </p>
        </Card>
      </div>;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'home') {
    return <HomePage modules={modules} state={state} selectedGroup={selectedGroup} displayName={displayName} onStartPractice={onStartPractice} onNavigateToModules={onNavigateToModules} onModuleSelect={onModuleSelect} onNavigateToAssessment={() => onNavigate('assessment')} onStartAssessment={onStartAssessment} onNavigateToLab={() => onNavigate('learningLab')} data-id="element-785" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'modules') {
    return <SubjectsPage modules={modules} progress={state.progress} selectedGroup={selectedGroup} onModuleSelect={onModuleSelect} onStartPractice={onStartPractice} data-id="element-786" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'learningLab') {
    return <LearningLabPage onStartAssessment={() => onNavigate('assessment' as NavView)} onStartMode={onStartMode} data-id="element-787" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'assessment') {
    return <AssessmentPage selectedGroup={selectedGroup} onStart={onStartAssessment} onViewReport={onViewReport} learnerId={learnerId} data-id="element-788" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'assessmentReport' && assessmentReport) {
    return <AssessmentReportPage report={assessmentReport} onExit={onExitReport} onRetake={onRetakeAssessment} onPracticeWeakAreas={onPracticeWeakAreas} onStartPracticeForModule={onStartPracticeForModule} data-id="element-789" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'progress') {
    return <ProgressPage state={state} selectedGroup={selectedGroup} onViewReport={onViewReport} data-id="element-790" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'mascot') {
    return <MascotCollectionPage state={state} selectedGroup={selectedGroup} data-id="element-791" />;
  }
  if (!requiresViewingLearnerSelection && currentNavView === 'mascot') {
    return <MascotCollectionPage state={state} selectedGroup={selectedGroup} data-id="element-792" />;
  }
  if (currentNavView === 'settings') {
    return <SettingsPage state={state} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onSetStartingPeriod={onSetStartingPeriod} onUpdateName={onUpdateName} accounts={accounts} onLogout={onLogout} viewingLearnerId={viewingLearnerId} data-id="element-793" />;
  }
  // Fallback: unknown view → render HomePage to prevent blank screen
  return <HomePage modules={modules} state={state} selectedGroup={selectedGroup} displayName={displayName} onStartPractice={onStartPractice} onNavigateToModules={onNavigateToModules} onModuleSelect={onModuleSelect} onNavigateToAssessment={() => onNavigate('assessment')} onStartAssessment={onStartAssessment} onNavigateToLab={() => onNavigate('learningLab')} data-id="element-794" />;
}